/**
 * Web Redirect Page
 * 
 * Authentication redirect/success page.
 * Shows a success message after authentication completes.
 * Typically used in OAuth flows or plugin pairing flows.
 */

import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuthLayoutTemplate, RedirectTemplate } from '@ideoz/ui';
import { useAppSelector, authService } from '@ideoz/core';

export default function Redirect(): JSX.Element {
    const [searchParams] = useSearchParams();
    const user = useAppSelector((state) => state.app.user);
    const [userEmail, setUserEmail] = useState<string>('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [isPairing, setIsPairing] = useState(false);
    const [pairingError, setPairingError] = useState<string | null>(null);
    const hasPairedRef = useRef(false);

    // Call pair-logged-user API when page loads with pairing_key
    useEffect(() => {
        const pairingKey = searchParams.get('pairing_key');
        const source = searchParams.get('figma_source');

        // Log for debugging
        if (pairingKey) {
            console.log('Redirect page - Pairing key:', pairingKey);
            console.log('Redirect page - Source:', source);
        }

        // Get user email if available
        if (user?.email) {
            setUserEmail(user.email);
        }

        // Call pair-logged-user API if pairing_key is present
        const pairUser = async () => {
            if (pairingKey && !hasPairedRef.current) {
                hasPairedRef.current = true;
                setIsPairing(true);
                setPairingError(null);
                
                try {
                    console.log('Calling pair-logged-user API with pairing key:', pairingKey);
                    await authService.pairLoggedUser(pairingKey);
                    console.log('User paired successfully');
                } catch (error: any) {
                    console.error('Error pairing user:', error);
                    // Don't block the redirect flow on pairing error
                    setPairingError(error?.message || 'Failed to pair user');
                } finally {
                    setIsPairing(false);
                }
            }
        };

        pairUser();
    }, [searchParams, user]);

    const handleRedirect = () => {
        if (isProcessing || isPairing) {
            console.log('Already processing or pairing in progress, skipping');
            return; // Prevent double clicks or clicking while pairing
        }

        setIsProcessing(true);

        const figmaSource = searchParams.get('figma_source');

        // Open Figma using deep link protocol
        // This will show the "Open Figma?" popup
        if (figmaSource === 'desktop') {
            console.log('Opening Figma desktop app...');
            window.location.href = 'figma://';
        } else if (figmaSource === 'figjam') {
            console.log('Opening FigJam app...');
            window.location.href = 'figjam://';
        } else {
            // Default to Figma if no source specified
            console.log('No figma_source specified, defaulting to Figma desktop app...');
            window.location.href = 'figma://';
        }

        // Reset processing state after a short delay to allow re-clicking if needed
        setTimeout(() => {
            setIsProcessing(false);
        }, 1000);

        // Don't redirect or close automatically - let the user interact with the popup
        // When they click "Open Figma", the browser will switch to Figma
        // The user can manually close this tab if needed
        console.log('Waiting for user to open Figma from popup...');
    };

    return (
        <AuthLayoutTemplate isWeb={true}>
            <RedirectTemplate
                isWeb={true}
                userEmail={userEmail}
                onRedirect={handleRedirect}
                isPairing={isPairing}
                pairingError={pairingError}
                isRedirectDisabled={isProcessing}
            />
        </AuthLayoutTemplate>
    );
}
